import { useEffect, type ReactNode } from "react";
import type { i18n as I18nInstance } from "i18next";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { ApolloProvider } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { I18nextProvider, useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { apolloClient } from "@/lib/graphql";
import { trackPageView } from "@/lib/analytics";
import { AuthProvider } from "@/lib/auth";
import { TourManager } from "@/components/tour/TourManager";
import AppRoutes from "./AppRoutes";
import ErrorBoundary from "./components/ErrorBoundary";
import CookieConsent from "./components/CookieConsent";
import ScrollToTop from "./components/ScrollToTop";
import ImpersonationBanner from "./components/ImpersonationBanner";
import LocaleSync from "./components/LocaleSync";

// The ONE render tree, shared by the browser (App.tsx) and the SSR sidecar
// (entrypoints/server.tsx). Hydration needs both sides to render the same tree:
// the same DOM, and the same component positions, since useId derives from them.
// When the two were hand-maintained copies, a skip link added to the browser
// copy only (PR #372) made React discard the server HTML on every
// server-rendered page. Anything that differs per side is a prop; anything
// added to the page chrome goes here, never into App.tsx or server.tsx.
// __tests__/ssr-hydration-parity.test.tsx hydrates one over the other.

interface AppShellProps {
  i18n: I18nInstance;
  queryClient: QueryClient;
  // BrowserRouter in the browser, StaticRouter (at the request URL) on the server.
  router: (children: ReactNode) => ReactNode;
  // The server collects the page's <head> tags through this; the browser omits it.
  helmetContext?: object;
}

export default function AppShell({ i18n, queryClient, router, helmetContext }: AppShellProps) {
  return (
    <I18nextProvider i18n={i18n}>
      <HelmetProvider context={helmetContext}>
        <ApolloProvider client={apolloClient}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <LocaleSync />
              <TooltipProvider>
                <Toaster />
                <Sonner />
                {router(
                  <>
                    <SkipLink />
                    <ScrollToTop />
                    <PageViewTracker />
                    <ImpersonationBanner />
                    <TourManager />
                    <CookieConsent />
                    <ErrorBoundary>
                      <div id="main-content" tabIndex={-1}>
                        <AppRoutes />
                      </div>
                    </ErrorBoundary>
                  </>,
                )}
              </TooltipProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ApolloProvider>
      </HelmetProvider>
    </I18nextProvider>
  );
}

// A keyboard user's first Tab stop: jumps past the header to the page content.
function SkipLink() {
  const { t } = useTranslation();
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-foreground focus:shadow-soft"
    >
      {t("appShell.skipToContent")}
    </a>
  );
}

// Fires a GA page_view on every client-side route change (gated on consent;
// no-op when GA isn't loaded, and effects never run on the server).
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}
